import type { FetchResult } from '@apollo/client';
import { ApolloLink } from '@apollo/client';
import * as _ from 'lodash-es';

type Data = FetchResult['data'];

function parseType(obj: NonNullable<Data>, type: string, key: string) {
  if ('__typename' in obj && obj.__typename === type) {
    const value = obj[key];
    if (typeof value === 'string') {
      return recursiveParse({
        ...obj,
        [key]: JSON.parse(value),
      });
    }
  }
  return false;
}

function recursiveParse(obj?: Data): Data {
  if (obj == null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(recursiveParse);
  }
  return (
    parseType(obj, 'Annotation', 'content') ||
    parseType(obj, 'Comment', 'content') ||
    parseType(obj, 'CommentThread', 'content') ||
    parseType(obj, 'Content', 'delta') ||
    parseType(obj, 'PublicComment', 'content') ||
    parseType(obj, 'CommentSearchResult', 'content') ||
    parseType(obj, 'Group', 'description') ||
    parseType(obj, 'GroupSearchResult', 'description') ||
    parseType(obj, 'History', 'delta') ||
    parseType(obj, 'History', 'diff') ||
    parseType(obj, 'History', 'snapshot') ||
    parseType(obj, 'Mention', 'content') ||
    parseType(obj, 'MentionPreview', 'content') ||
    parseType(obj, 'Post', 'content') ||
    parseType(obj, 'PostSearchResult', 'content') ||
    parseType(obj, 'PostSearchResult', 'highlight') ||
    parseType(obj, 'PublicPostSearchResult', 'content') ||
    parseType(obj, 'PublicPostSearchResult', 'highlight') ||
    parseType(obj, 'PublicTopicSearchResult', 'description') ||
    parseType(obj, 'PublicPost', 'content') ||
    parseType(obj, 'PublicTopic', 'description') ||
    parseType(obj, 'CommentThread', 'context') ||
    parseType(obj, 'ReactionThread', 'context') ||
    parseType(obj, 'Topic', 'description') ||
    parseType(obj, 'TopicSearchResult', 'description') ||
    parseType(obj, 'User', 'description') ||
    parseType(obj, 'UserSearchResult', 'description') ||
    parseType(obj, 'SlackMessage', 'delta') ||
    _.reduce(
      obj,
      (memo, value, key) => {
        // eslint-disable-next-line no-param-reassign
        memo[key] = recursiveParse(value);
        return memo;
      },
      {} as Record<string, Data>,
    )
  );
}

export default new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => ({
    ...response,
    data: recursiveParse(response.data),
  }));
});
